import { omit, isEmpty, uniqueId, toLower } from 'lodash';
import { axios, axiosHeaderless } from './common';
import AxiosAbort from 'axios';
import {
  CM_BASE_URL,
  DOWNLOAD_SCREENS
} from '../../pages/constants/cmConstants';
import {
  downloadFile as downloadFileFromData,
  showFileDownloadingNotification,
  showFileDownloadSucessNotification,
  showFileDownloadFailedNotification,
  getFileNameFromContentDisposition
} from '../../pages/cm/utils/common';
import {
  convertFilterEpochForBackend,
  getDownloadTimezone
} from '../../util/timezone';

export const downloadReport = ({
  filters = {},
  selectedIds = [],
  fileName,
  screen,
  setLoading
}) => {
  const uuid = uniqueId();
  showFileDownloadingNotification({
    key: uuid,
    fileName: `'${fileName}' report`
  });
  let params = {};
  let url;

  if (screen === DOWNLOAD_SCREENS.ALERT_RETRY_HISTORY) {
    params['status'] = filters?.status;
    params['isLatest'] = filters?.isLatest;

    url = `${CM_BASE_URL}/${screen}/all`;
  } else {
    params =
      selectedIds.length > 0
        ? {
            [screen === 'cases' ? 'caseIds' : 'id']: selectedIds.join(','),
            department: filters?.department
          }
        : {
            'assigned-user': !isEmpty(filters?.assignedUser)
              ? filters.assignedUser.join(',')
              : null,
            ...(filters.approvalStatus
              ? { 'checker-status': filters.approvalStatus }
              : ''),
            ...omit(filters, [
              'alertLevel',
              'assignedUser',
              'sourceIds',
              'searchField',
              'searchText',
              'typologyIds',
              'approvalStatus'
            ]),
            from: convertFilterEpochForBackend(filters.from),
            to: convertFilterEpochForBackend(filters.to)
          };

    params[screen === 'cases' ? 'alert-level' : 'level'] = filters.alertLevel;

    if (!isEmpty(filters.typologyIds)) {
      params['typologyIds'] = filters.typologyIds.join(',');
    }

    if (!isEmpty(filters.sourceIds)) {
      params['sources'] = filters.sourceIds.join(',');
    }

    url = `${CM_BASE_URL}/${screen}${screen === 'cases' ? '/all' : ''}`;
  }

  params['type'] = 'excel';

  if (filters.searchField && filters.searchText) {
    params[filters.searchField] = toLower(filters.searchText);
  }
  
  axios
    .get(url, {
      params: params,
      responseType: 'blob',
      // PER-69: render exported timestamps in the per-client download timezone
      // (config.js `downloadTimezoneMode`): browser-local by default (matches the
      // local-time UI), or UTC for clients that store local-as-UTC + timeAdjustment
      // (e.g. GCash). A valid Time-Zone header is required by these Excel endpoints
      // (BadRequest "Please provide a valid timezone..." if absent).
      headers: { 'Time-Zone': getDownloadTimezone() }
    })
    .then((res) => {
      if (res.status === 200) {
        const fileName = getFileNameFromContentDisposition(
          res.headers['content-disposition']
        );
        showFileDownloadingNotification({ key: uuid, fileName });
        try {
          downloadFileFromData(res.data, fileName);
          showFileDownloadSucessNotification({ key: uuid, fileName });
          setLoading(false);
        } catch (e) {
          showFileDownloadFailedNotification({ key: uuid, fileName });
          setLoading(false);
        }
      } else {
        showFileDownloadFailedNotification({
          key: uuid,
          fileName: `'${fileName}' report`
        });
        setLoading(false);
      }
    });
};

const downloadFileByURL = async (url, fileName, uuid) => {
  await axiosHeaderless({
    url: url,
    method: 'GET',
    responseType: 'blob'
  })
    .then((res) => {
      if (res.status === 200) {
        downloadFileFromData(res.data, fileName);
        showFileDownloadSucessNotification({ key: uuid, fileName });
      } else {
        showFileDownloadFailedNotification({ key: uuid, fileName });
      }
    })
    .catch((err) => {
      showFileDownloadFailedNotification({ key: uuid, fileName });
    });
};

export const downloadFile = async (fileId, fileName) => {
  const uuid = uniqueId();
  showFileDownloadingNotification({ key: uuid, fileName });

  const url = `${CM_BASE_URL}/files/${fileId}`;
  await axios
    .get(url, {
      responseType: 'blob'
    })
    .then((res) => {
      if (res.status === 200) {
        // If CSV, prepend UTF-8 BOM to preserve correct encoding in Excel and viewers
        const contentType = res.headers['content-type'] || '';
        let data = res.data;
        if (
          /\.csv$/i.test(fileName || '') ||
          contentType.startsWith('text/csv')
        ) {
          const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
          data = new Blob([bom, res.data], { type: 'text/csv;charset=UTF-8' });
        }
        downloadFileFromData(data, fileName);
        showFileDownloadSucessNotification({ key: uuid, fileName });
      } else if (res.status === 204) {
        downloadFileByURL(res.headers.location, fileName, uuid);
      } else {
        showFileDownloadFailedNotification({ key: uuid, fileName });
      }
    })
    .catch((err) => {
      showFileDownloadFailedNotification({ key: uuid, fileName });
    });
};

export const getNotification = async () => {
  const url = `${CM_BASE_URL}/on-screen-notification`;
  return axios.get(url).then((res) => {
    if (res.status !== 200) {
      throw new Error('Failed to fetch notification data');
    }
    return res.data;
  });
};

export const getEmailPreferences = async ({ setLoading }) => {
  const url = `${CM_BASE_URL}/system/prefs/notification`;
  return axios.get(url).then((res) => {
    setLoading(false);
    if (res.status !== 200) {
      throw new Error('Failed to fetch email configuration');
    }
    return res.data;
  });
};

export const updateEmailPreferences = async ({ setLoading, preferences }) => {
  const url = `${CM_BASE_URL}/system/prefs/notification`;
  return axios.put(url, preferences).then((res) => {
    setLoading(false);
    if (res.status !== 200) {
      throw new Error('Failed to update email configuration');
    }
    return res.data;
  });
};

export const getGeneralSettings = async ({ setLoading, configKey }) => {
  const url = `${CM_BASE_URL}/general-settings`;
  const params = {
    'config-key': configKey
  };

  return axios.get(url, { params }).then((res) => {
    setLoading(false);
    if (res.status !== 200) {
      throw new Error(res?.data?.error);
    }
    return res.data;
  });
};

export const getAutoHibernationConfig = async () => {
  const url = `${CM_BASE_URL}/auto-hibernation-configuration`;

  return axios.get(url).then((res) => {
    if (res.status !== 200) {
      throw new Error(res?.data?.error);
    }
    return res.data;
  });
};

export const updateAutoHibernationConfig = async ({ settings }) => {
  const url = `${CM_BASE_URL}/auto-hibernation-configuration`;

  return axios.put(url, settings).then((res) => {
    if (res.status !== 200 && res.status !== 201) {
      throw new Error(res?.data?.error);
    }
    return res.data;
  });
};

export const getAgeSettings = async ({ setLoading }) => {
  const url = `${CM_BASE_URL}/general-settings/ageSettings`;

  return axios.get(url).then((res) => {
    setLoading(false);
    if (res.status !== 200) {
      throw new Error(res?.data?.error);
    }
    return res.data;
  });
};

export const updateAgeSettings = async ({ setLoading, settings }) => {
  const url = `${CM_BASE_URL}/general-settings/ageSettings/config-value`;

  return axios.put(url, settings).then((res) => {
    setLoading(false);
    if (res.status !== 200) {
      console.log(res?.data?.error);
      throw new Error(res?.data?.error);
    }
    return res.data;
  });
};

export const updateGeneralSettings = async ({ setLoading, id, settings }) => {
  const url = `${CM_BASE_URL}/general-settings/${id}/config-value`;

  return axios.put(url, settings).then((res) => {
    setLoading(false);
    if (res.status !== 200) {
      throw new Error(res?.data?.error);
    }
    return res.data;
  });
};

export const fetchTypologies = async () => {
  const url = `${CM_BASE_URL}/typologies`;

  return axios.get(url).then((res) => {
    if (res.status !== 200) {
      throw new Error(res?.data?.error);
    }

    return res.data;
  });
};

export const createManualField = async ({ setLoading, caseFields }) => {
  const url = `${CM_BASE_URL}/custom-fields`;

  return axios.post(url, caseFields).then((res) => {
    setLoading(false);

    if (res.status !== 201) {
      if (res?.data?.code === 'FIELD_DUPLICATE') {
        throw new Error(
          `Field Name '${caseFields.label}' already exists. Please try a different name.`
        );
      }

      throw new Error('Failed to update Manual Case Fields');
    }
    return res.data;
  });
};

export const getManualField = async ({ setLoading }) => {
  const url = `${CM_BASE_URL}/custom-fields `;

  return axios.get(url).then((res) => {
    setLoading(false);

    if (res.status !== 200) {
      throw new Error('Failed to fetch manual fields');
    }
    return res.data;
  });
};

export const deleteManualField = async ({ setLoading, deleteId }) => {
  const url = `${CM_BASE_URL}/custom-fields/${deleteId}`;

  return axios.delete(url).then((res) => {
    setLoading(false);
    if (res.status !== 200) {
      throw new Error('Failed to delete existing manual field');
    }
    return res.data;
  });
};

export const getRegulatoryReports = async (filters) => {
  const url = `${CM_BASE_URL}/files`;

  return axios.get(url, { params: filters }).then((res) => {
    if (res.status !== 200) {
      throw new Error(
        res?.data?.error ||
          'Error in fetching report details. Please try again after sometime.'
      );
    }

    return res.data;
  });
};

export const getRegulatoryCTRReports = async (filters) => {
  const url = `${CM_BASE_URL}/reports/getAllCTR`;

  return axios.get(url, { params: filters }).then((res) => {
    if (res.status !== 200) {
      throw new Error(
        res?.data?.error ||
          'Error in fetching report details. Please try again after sometime.'
      );
    }

    return res.data;
  });
};

export const getReportFilterOptions = async (reportType) => {
  const url = `${CM_BASE_URL}/files/dropdowns`;
  const params = { 'doc-category': reportType };

  return axios.get(url, { params }).then((res) => {
    if (res.status !== 200) {
      throw new Error(
        res?.data?.error ||
          'Error in fetching filter details. Please try again after sometime.'
      );
    }

    return res.data;
  });
};

export const downloadOperationalReport = async (filters) => {
  const uuid = uniqueId();
  showFileDownloadingNotification({
    key: uuid,
    fileName: `'${filters.reportTypeLabel}' report`
  });

  const url = `${CM_BASE_URL}/files/types/${filters.reportType}`;
  await axios
    .get(url, {
      responseType: 'blob',
      params: {
        // FIN-10470: align the operational-report date window with the displayed
        // day (timeAdjustment-aware) so it is consistent with the list/exports.
        from: convertFilterEpochForBackend(filters.from),
        to: convertFilterEpochForBackend(filters.to)
      },
      // PER-69: use the per-client download timezone (config.js downloadTimezoneMode)
      // rather than the raw browser zone, consistent with the other CM downloads.
      headers: { 'Time-Zone': getDownloadTimezone() }
    })
    .then((res) => {
      if (res.status === 200) {
        const fileName = getFileNameFromContentDisposition(
          res.headers['content-disposition']
        );
        showFileDownloadingNotification({ key: uuid, fileName });
        try {
          downloadFileFromData(res.data, fileName);
          showFileDownloadSucessNotification({ key: uuid, fileName });
        } catch (e) {
          showFileDownloadFailedNotification({ key: uuid, fileName });
        }
      } else {
        showFileDownloadFailedNotification({
          key: uuid,
          fileName: `'${filters.reportTypeLabel}' report`
        });
      }
    });
};

export const fetchSources = async () => {
  const url = `${CM_BASE_URL}/configs/sources`;

  return axios.get(url).then((res) => {
    if (res.status !== 200) {
      throw new Error(res?.data?.error);
    }

    return res.data;
  });
};

/**
 * Fetches watchlist options for dropdown filters.
 *
 * @param {string} field - The watchlist field to fetch (e.g., 'category', 'subcategory')
 * @param {Object} params - Query parameters
 * @param {number} params.offset - Pagination offset
 * @param {number} params.limit - Pagination limit
 * @param {string} [params.search] - Optional search query
 * @param {boolean} [params.forCases] - When true, only return values from alerts that have become cases
 * @param {Object} [options] - Additional options
 * @param {AbortSignal} [options.signal] - AbortController signal for request cancellation
 * @returns {Promise<{payload: string[], total: number}>} The watchlist options response
 */
export const fetchWatchlistOptions = async (field, params, { signal } = {}) => {
  const url = `${CM_BASE_URL}/alerts/watchlist/${field}`;
  const queryParams = {
    offset: params.offset,
    limit: params.limit
  };
  if (params.search) {
    queryParams.search = params.search;
  }
  // Add forCases parameter when filtering for case listing page
  if (params.forCases) {
    queryParams.forCases = params.forCases;
  }

  try {
    const res = await axios.get(url, {
      params: queryParams,
      signal
    });

    if (res.status !== 200) {
      throw new Error(res?.data?.error || 'Unknown error');
    }

    return res.data;
  } catch (err) {
    // Rethrow abort errors so your fetchMoreOptions can handle them
    if (AxiosAbort.isCancel(err)) {
      throw new DOMException('Aborted', 'AbortError');
    }
    throw err;
  }
};
